// @ts-check

import {
  predictiveRoutingService, $ngStateService
} from '@/angularBridgeCode/ringbaPortal.servicesModule';
import {
  SET_PREDICTIVE_ROUTING,
  ADD_PREDICTIVE_ROUTING_CONFIG,
  REPLACE_PREDICTIVE_ROUTING_CONFIG,
  SET_PREDICTIVE_ROUTING_CONFIG_REFERENCES
} from './mutationTypes';
import {
  GET_PREDICTIVE_ROUTING,
  GET_PREDICTIVE_ROUTING_CONFIG,
  CREATE_PREDICTIVE_ROUTING_CONFIG,
  DELETE_PREDICTIVE_ROUTING_CONFIG,
  UPDATE_PREDICTIVE_ROUTING_CONFIG,
  GET_PREDICTIVE_ROUTING_CONFIG_REFERENCES,
  CONVERT_PREDICTIVE_ROUTING_CONFIG_TO_ADVANCED,
  CONVERT_PREDICTIVE_ROUTING_CONFIG_TO_BASIC
} from './actionTypes';

import {
  SET_ACTION_STATUS_PENDING,
  SET_ACTION_STATUS_SUCCESS,
  SET_ACTION_STATUS_ERROR
} from '@/store/helpers';


/** @type {import('vuex').ActionTree} */
const actions = {

  /**
   * Get all Predictive Routing
   */
  async [GET_PREDICTIVE_ROUTING]({ rootState, commit }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_PREDICTIVE_ROUTING });
    try {
      const predictiveRouting = await predictiveRoutingService.getPredictiveRouting(rootState.currentAccountId);
      commit(SET_PREDICTIVE_ROUTING, predictiveRouting);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_PREDICTIVE_ROUTING });
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: GET_PREDICTIVE_ROUTING, error: e });
    }
  },

  /**
   * Get Predictive Routing Config
   */
  async [GET_PREDICTIVE_ROUTING_CONFIG]({ rootState, commit }, configId) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_PREDICTIVE_ROUTING_CONFIG });
    try {
      const predictiveRoutingConfig = await predictiveRoutingService.getPredictiveRoutingConfig(rootState.currentAccountId, configId);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_PREDICTIVE_ROUTING_CONFIG });
      return predictiveRoutingConfig;
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: GET_PREDICTIVE_ROUTING_CONFIG, error: e });
    }
  },

  /**
   * Create Predictive Routing Config
   */
  async [CREATE_PREDICTIVE_ROUTING_CONFIG]({ rootState, commit }, config) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: CREATE_PREDICTIVE_ROUTING_CONFIG });
    try {
      const response = await predictiveRoutingService.createPredictiveRoutingConfig(rootState.currentAccountId, config);
      commit(ADD_PREDICTIVE_ROUTING_CONFIG, response.data.rpcSettingItem);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: CREATE_PREDICTIVE_ROUTING_CONFIG });
      return response.data;
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: CREATE_PREDICTIVE_ROUTING_CONFIG, error: e });
    }
  },

  /**
   * Update Predictive Routing Config
   */
  async [UPDATE_PREDICTIVE_ROUTING_CONFIG]({ rootState, commit }, config) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: UPDATE_PREDICTIVE_ROUTING_CONFIG });
    try {
      const response = await predictiveRoutingService.updatePredictiveRoutingConfig(rootState.currentAccountId, config);
      commit(REPLACE_PREDICTIVE_ROUTING_CONFIG, response.data.rpcSettingItem);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: UPDATE_PREDICTIVE_ROUTING_CONFIG });
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: UPDATE_PREDICTIVE_ROUTING_CONFIG, error: e });
    }
  },

  /**
   * Delete Predictive Routing Config
   */
  async [DELETE_PREDICTIVE_ROUTING_CONFIG]({ rootState, commit, dispatch }, configId) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: DELETE_PREDICTIVE_ROUTING_CONFIG });
    try {
      await predictiveRoutingService.deletePredictiveRoutingConfig(rootState.currentAccountId, configId);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: DELETE_PREDICTIVE_ROUTING_CONFIG });
      dispatch(GET_PREDICTIVE_ROUTING);
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: DELETE_PREDICTIVE_ROUTING_CONFIG, error: e });
    }
  },

  /**
   * Get references using Predictive Routing Config
   */
  async [GET_PREDICTIVE_ROUTING_CONFIG_REFERENCES]({ rootState, commit }, configId) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_PREDICTIVE_ROUTING_CONFIG_REFERENCES });
    try {
      const response = await predictiveRoutingService.getPredictiveRoutingConfigReferences(rootState.currentAccountId, configId);
      // Assemble all the types of references
      const references = [];
      if (response.data.buyers) {
        references.push(...response.data.buyers.map(buyer => {
          return {
            type: 'Buyer',
            name: buyer.buyerName,
            id: buyer.buyerId,
            link: $ngStateService.href('dashboard.buyer.edit', { buyerId: buyer.buyerId }, {absolute: true})
          };
        }));
      }

      if (response.data.campaigns) {
        references.push(...response.data.campaigns.map(campaign => {
          return {
            type: 'Campaign',
            name: campaign.campaignName,
            id: campaign.campaignId,
            link: $ngStateService.href('dashboard.campaign', { campaignId: campaign.campaignId }, { absolute: true })
          };
        }));
      }

      if (response.data.targets) {
        references.push(...response.data.targets.map(target => {
          return {
            type: 'Target',
            name: target.targetName,
            id: target.targetId,
            link: $ngStateService.href('dashboard.target.edit', { targetId: target.targetId }, {absolute: true})
          };
        }));
      }

      if (response.data.pingtreetargets) {
        references.push(...response.data.pingtreetargets.map(pingtreetarget => {
          return {
            type: 'Ring Tree Target',
            name: pingtreetarget.pingTreeTargetName,
            id: pingtreetarget.pingTreeTargetId,
            link: $ngStateService.href('dashboard.ringtreetarget.edit', { pingTreeTargetId: pingtreetarget.pingTreeTargetId }, {absolute: true})
          };
        }));
      }

      commit(SET_PREDICTIVE_ROUTING_CONFIG_REFERENCES, references);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_PREDICTIVE_ROUTING_CONFIG_REFERENCES });
    } catch (e) {
      commit(SET_PREDICTIVE_ROUTING_CONFIG_REFERENCES, []);
      commit(SET_ACTION_STATUS_ERROR, { actionName: GET_PREDICTIVE_ROUTING_CONFIG_REFERENCES, error: e });
    }
  },

  /**
   * Convert Predictive Routing Config to Advanced
   */
  async [CONVERT_PREDICTIVE_ROUTING_CONFIG_TO_ADVANCED]({ rootState, commit }, payload) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: CONVERT_PREDICTIVE_ROUTING_CONFIG_TO_ADVANCED });
    try {
      const response = await predictiveRoutingService.convertPredictiveRoutingConfigToAdvanced(rootState.currentAccountId, payload);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: CONVERT_PREDICTIVE_ROUTING_CONFIG_TO_ADVANCED });
      return response;
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: CONVERT_PREDICTIVE_ROUTING_CONFIG_TO_ADVANCED, error: e });
    }
  },

  /**
   * Convert Predictive Routing Config to Basic
   */
  async [CONVERT_PREDICTIVE_ROUTING_CONFIG_TO_BASIC]({ rootState, commit }, payload) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: CONVERT_PREDICTIVE_ROUTING_CONFIG_TO_BASIC });
    try {
      const response = await predictiveRoutingService.convertPredictiveRoutingConfigToBasic(rootState.currentAccountId, payload);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: CONVERT_PREDICTIVE_ROUTING_CONFIG_TO_BASIC });
      return response;
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: CONVERT_PREDICTIVE_ROUTING_CONFIG_TO_BASIC, error: e });
    }
  },
};

export default actions;